import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { FileText, Image, Video, Film, Settings, ChevronLeft, ChevronRight, Lock, Construction, Home, Calendar, PoundSterling, Mic, BrainCircuit, Users, Clapperboard, Search, Calculator, ScrollText, Hourglass, PackageCheck, ArrowRight, Archive, X, Presentation } from "lucide-react";
import { useAuth } from "./AuthContext";
import { AppHeader } from "./AppHeader";
import MyWmsOrders from "./MyWmsOrders";
import "./ModuleSelector.css";

// Legacy / coming-soon tools: never on the carousel; super admins reach them from the Archive drawer (bottom-left).
const ARCHIVED = {
  social: "Legacy",
  video: "Legacy",
  presentour: "Coming soon",
  "social-week": "Coming soon",
  "valuation-tools": "Legacy",
  "property-description": "Legacy",
  "avatar-onboarding": "Legacy",
};

const ALL_TOOLS = [
  {
    key: "team",
    name: "Team",
    description: "Business area org charts, drag-and-drop team management and traffic light reports",
    cta: "Open Team",
    Icon: Users,
    iconClass: "management-icon",
    adminOnly: true,
  },
  {
    key: "proposals",
    name: "Proposals",
    description: "Social pitch and New Business proposals, plus the prospects map",
    cta: "Open",
    Icon: Presentation,
    iconClass: "proposals-icon",
    adminOnly: true,
  },
  {
    key: "script",
    name: "Script Builder",
    description: "Generate natural, engaging voiceover scripts for property video tours",
    cta: "Get Started",
    Icon: FileText,
    iconClass: "script-icon",
  },
  {
    key: "social",
    name: "Social Image Builder",
    description: "Transform property photos into social-ready images with branding",
    cta: "Get Started",
    Icon: Image,
    iconClass: "social-icon",
  },
  {
    key: "video",
    name: "Social Video",
    description: "Create vertical social versions, optional stitched edits, and logo overlays",
    cta: "Get Started",
    Icon: Video,
    iconClass: "video-icon",
  },
  {
    key: "presentour",
    name: "Presentour Video",
    description: "Create videos synced to your script timing for perfect voiceover matching",
    cta: "Get Started",
    Icon: Film,
    iconClass: "presentour-icon",
  },
  {
    key: "management",
    name: "Management",
    description: "View and manage your branches, agents, and avatars",
    cta: "Open",
    Icon: Settings,
    iconClass: "management-icon",
  },
  {
    key: "listings",
    name: "My Listings",
    description: "View your branch property listings and order social packages",
    cta: "View Listings",
    Icon: Home,
    iconClass: "listings-icon",
  },
  {
    key: "reels",
    name: "Reels",
    description: "Price Reduction and Sold reels rebuilt from a property's finished Sneak Peek — same footage, your brand, new message",
    cta: "Make a Reel",
    Icon: Film,
    iconClass: "reels-icon",
  },
  {
    key: "social-week",
    name: "Social Week",
    description: "Generate a full week of social media content from your listings",
    cta: "Build Week",
    Icon: Calendar,
    iconClass: "social-week-icon",
  },
  {
    key: "social-hub",
    name: "Social Library",
    description: "Your finished Sneak Peeks, reels and avatar videos — preview, download, or grab a whole order in one go",
    cta: "Open Hub",
    Icon: Clapperboard,
    iconClass: "social-hub-icon",
  },
  {
    key: "valuation-tools",
    name: "Post Valuation",
    description: "Free AI tools to help you win more instructions — covering notes, follow-up scripts and more",
    cta: "Open Tools",
    Icon: PoundSterling,
    iconClass: "valuation-tools-icon",
    freeBadge: true,
  },
  {
    key: "property-description",
    name: "Property Description",
    description: "AI listing copy in your branch tone — CPR & BPR compliance built in",
    cta: "Write Description",
    Icon: FileText,
    iconClass: "property-description-icon",
    freeBadge: true,
  },
  {
    key: "avatar-onboarding",
    name: "Avatar Onboarding",
    description: "Generate a 20-30 minute voice-clone training pack tailored to your agency and town",
    cta: "Build Pack",
    Icon: Mic,
    iconClass: "avatar-onboarding-icon",
    freeBadge: true,
  },
  {
    key: "employment-intelligence",
    name: "Employment Intelligence",
    description: "AI-powered workforce analytics — DISC, attrition, hiring insights and team intelligence",
    cta: "Open Hub",
    Icon: BrainCircuit,
    iconClass: "employment-intelligence-icon",
    freeBadge: true,
    adminOnly: true,
  },
];

const SUPER_ADMIN_TOOLS = [
  {
    key: "editor",
    name: "Video Team",
    description: "The editors' hub — assignments, uploads, briefs and the team's My Listings",
    cta: "Open Hub",
    Icon: Clapperboard,
    iconClass: "video-team-icon",
  },
  {
    key: "video-search",
    name: "Order Search",
    description: "Find any WMS order by ID, then open it in the Video Editor or Social Lite",
    cta: "Search Orders",
    Icon: Search,
    iconClass: "video-search-icon",
  },
  {
    key: "cost-centre",
    name: "Cost Centre",
    description: "What every render, AI analysis and script costs in credits and £ — with worked examples per property",
    cta: "See Costs",
    Icon: Calculator,
    iconClass: "cost-centre-icon",
  },
];

const ADMIN_TOOL = {
  key: "admin",
  name: "Admin Panel",
  description: "Manage branches, agents, avatars, and user access",
  cta: "Manage",
  Icon: Settings,
  iconClass: "admin-icon",
};

// Determine which tools a role can access, which are greyed teasers, and which are hidden
function getToolAccess(role) {
  switch (role) {
    case "super_admin":
      return { active: ["team", "proposals", "script", "video-search", "cost-centre", "editor", "admin"], greyed: [], showAdmin: true };
    case "client_manager":
      return { active: ["script", "admin", "listings", "social-hub", "reels"], greyed: [], showAdmin: true };
    case "md":
      return { active: ["team", "proposals"], greyed: [], showAdmin: false };
    case "founder":
      return { active: ["proposals"], greyed: [], showAdmin: false };
    case "editor":
      return { active: ["script"], greyed: [], showAdmin: false };
    case "branch_manager":
    case "branch_admin":
      return { active: ["script", "listings", "social-hub", "reels"], greyed: [], showAdmin: false };
    case "estate_agent":
    case "agent":
      return { active: ["script", "listings", "social-hub", "reels"], greyed: [], showAdmin: false };
    default:
      return { active: ["script"], greyed: [], showAdmin: false };
  }
}

function ModuleSelector({ onSelectModule }) {
  const { user } = useAuth();
  const role = user?.role || "estate_agent";
  const isEditor = role === "editor";
  const [serverAccess, setServerAccess] = useState(null);

  // Roles & Access (Admin → Users) is the source of truth; the hardcoded rules are the fallback.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/access/me`)
      .then((r) => { if (!cancelled) setServerAccess(r.data); })
      .catch(() => { if (!cancelled) setServerAccess(false); });
    return () => { cancelled = true; };
  }, [user?.id, user?.role, user?.demo_user]);

  // Social Week is a paid bolt-on. Super admin and staff always see it
  // unlocked; branch users see a locked tile that pitches NicheSocial.
  const isStaff = role === "super_admin" || role === "client_manager" || role === "editor";
  const isBranchUser = ["agent", "estate_agent", "branch_manager", "branch_admin"].includes(role);
  const isAgencyLeader = ["brand_manager", "group_manager", "md", "founder"].includes(role);
  const socialWeekLocked = !isStaff && !user?.social_week_access;

  const access = useMemo(() => {
    if (serverAccess && Array.isArray(serverAccess.tools)) {
      const active = serverAccess.tools.filter((t) => t.state === "on").map((t) => t.key);
      const greyed = serverAccess.tools.filter((t) => t.state === "soon").map((t) => t.key);
      return { active: active.filter((k) => k !== "admin"), greyed, showAdmin: active.includes("admin") };
    }
    const a = getToolAccess(role);
    // Demo users (e.g. demo agents) get Social Video unlocked on top of their role's tools
    if (user?.demo_user && !a.active.includes("video")) {
      return { ...a, active: [...a.active, "video"] };
    }
    return a;
  }, [role, user?.demo_user, serverAccess]);

  const tools = useMemo(() => {
    const allowed = new Set([...access.active, ...access.greyed]);
    const list = [...ALL_TOOLS, ...SUPER_ADMIN_TOOLS].filter(t => allowed.has(t.key) && !ARCHIVED[t.key]).map(t => ({
      ...t,
      disabled: access.greyed.includes(t.key),
      underConstruction: (access.underConstruction || []).includes(t.key),
      locked: t.key === "social-week" && socialWeekLocked,
      demoBadge: t.key === "video" && !!user?.demo_user,
    }));
    return list;
  }, [access, socialWeekLocked, user?.demo_user]);

  const [activeIndex, setActiveIndex] = useState(0);

  // Product tour asks the carousel to bring a tool card to the centre
  useEffect(() => {
    const onFocus = (e) => { const i = tools.findIndex((t) => t.key === e.detail); if (i >= 0) setActiveIndex(i); };
    window.addEventListener('nichesocial:tour-focus-tool', onFocus);
    return () => window.removeEventListener('nichesocial:tour-focus-tool', onFocus);
  }, [tools]);

  const goPrev = () => setActiveIndex((i) => (i - 1 + tools.length) % tools.length);
  const goNext = () => setActiveIndex((i) => (i + 1) % tools.length);

  const getPosition = (idx) => {
    const diff = (idx - activeIndex + tools.length) % tools.length;
    if (diff === 0) return "center";
    if (diff === 1) return "right";
    if (diff === tools.length - 1) return "left";
    if (diff === 2) return "far-right";
    if (diff === tools.length - 2) return "far-left";
    return "hidden";
  };

  const handleCardClick = (tool, pos) => {
    if (pos !== "center") {
      setActiveIndex(tools.indexOf(tool));
      return;
    }
    if (tool.disabled) return;
    onSelectModule(tool.key);
  };

  return (
    <div className="module-selector">
      <AppHeader
        onBack={isEditor ? () => onSelectModule('editor') : undefined}
        backLabel="Back to Dashboard"
        arrowRight
      />

      <div className="module-content">
        {isBranchUser && <AgentStrip onSelectModule={onSelectModule} />}
        {(isBranchUser || isAgencyLeader) && <MyWmsOrders onSelectModule={onSelectModule} />}
        <div className="module-intro">
          <h1 className="module-title">Visual Marketing Tools</h1>
          <p className="module-subtitle">
            Internal production tools for estate agent media teams
          </p>
        </div>

        <div className="carousel-wrapper" data-testid="tools-carousel">
          {tools.length > 1 && (
            <button className="carousel-arrow carousel-arrow-left" onClick={goPrev} data-testid="carousel-prev">
              <ChevronLeft size={28} />
            </button>
          )}

          <div className="carousel-track">
            {tools.map((tool, idx) => {
              const pos = getPosition(idx);
              return (
                <button
                  key={tool.key}
                  className={`carousel-card ${pos} ${tool.disabled ? "card-disabled" : ""} ${tool.underConstruction ? "card-under-construction" : ""} ${tool.locked ? "card-locked" : ""}`}
                  onClick={() => handleCardClick(tool, pos)}
                  data-testid={`tool-card-${tool.key}`}
                >
                  {tool.freeBadge && (
                    <span className="module-free-badge" data-testid={`tool-free-${tool.key}`}>
                      Free
                    </span>
                  )}
                  {tool.demoBadge && (
                    <span className="module-free-badge module-demo-badge" data-testid={`tool-demo-${tool.key}`}>
                      Demo
                    </span>
                  )}
                  <div className={`module-icon-wrapper ${tool.iconClass}`}>
                    <tool.Icon className="module-icon" />
                    {tool.locked && (
                      <span className="module-lock-badge" data-testid={`tool-lock-${tool.key}`}>
                        <Lock size={14} />
                      </span>
                    )}
                  </div>
                  <h2 className="module-name">{tool.name}</h2>
                  <p className="module-description">
                    {tool.locked ? "Not included in your package. Tap to discover NicheSocial — done-for-you social media for your branch." : tool.description}
                  </p>
                  {tool.disabled && pos === "center" && (
                    <span className="module-locked"><Lock size={14} /> Coming Soon</span>
                  )}
                  {tool.underConstruction && pos === "center" && (
                    <span className="module-construction"><Construction size={14} /> Under Construction</span>
                  )}
                  {tool.locked && pos === "center" && (
                    <span className="module-cta module-cta-upgrade" data-testid={`tool-upgrade-${tool.key}`}>
                      Discover NicheSocial &rarr;
                    </span>
                  )}
                  {!tool.disabled && !tool.underConstruction && !tool.locked && pos === "center" && (
                    <span className="module-cta">{tool.cta} &rarr;</span>
                  )}
                </button>
              );
            })}
          </div>

          {tools.length > 1 && (
            <button className="carousel-arrow carousel-arrow-right" onClick={goNext} data-testid="carousel-next">
              <ChevronRight size={28} />
            </button>
          )}
        </div>

        <div className="carousel-dots">
          {tools.map((tool, idx) => (
            <button
              key={tool.key}
              className={`carousel-dot ${idx === activeIndex ? "active" : ""}`}
              onClick={() => setActiveIndex(idx)}
              data-testid={`carousel-dot-${idx}`}
            />
          ))}
        </div>
      </div>
      {role === "super_admin" && <ArchiveDrawer onSelectModule={onSelectModule} />}
    </div>
  );
}

export default ModuleSelector;


// Super admin only: legacy / coming-soon tools live here instead of on the carousel.
function ArchiveDrawer({ onSelectModule }) {
  const [open, setOpen] = useState(false);
  const items = ALL_TOOLS.filter((t) => ARCHIVED[t.key]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <>
      <button className="archive-fab" onClick={() => setOpen(true)} title="Archive — legacy & coming-soon tools" aria-label="Open archive" data-testid="archive-drawer-toggle">
        <Archive size={20} />
      </button>
      {open && <div className="archive-backdrop" onClick={() => setOpen(false)} data-testid="archive-drawer-backdrop" />}
      <aside className={`archive-drawer ${open ? "is-open" : ""}`} aria-hidden={!open} data-testid="archive-drawer">
        <div className="archive-head">
          <span className="archive-head-icon"><Archive size={18} /></span>
          <div>
            <h3>Archive</h3>
            <p>Legacy and coming-soon tools. Super admin only.</p>
          </div>
          <button className="archive-close" onClick={() => setOpen(false)} aria-label="Close archive" data-testid="archive-drawer-close"><X size={18} /></button>
        </div>
        <div className="archive-list">
          {items.map((t) => (
            <button key={t.key} className="archive-row" onClick={() => { setOpen(false); onSelectModule(t.key); }} data-testid={`archive-tool-${t.key}`}>
              <span className={`archive-row-icon module-icon-wrapper ${t.iconClass}`}><t.Icon size={22} /></span>
              <span className="archive-row-text">
                <strong>{t.name}</strong>
                <small>{t.description}</small>
              </span>
              <span className={`archive-tag ${ARCHIVED[t.key] === "Coming soon" ? "is-soon" : ""}`}>{ARCHIVED[t.key]}</span>
            </button>
          ))}
        </div>
      </aside>
    </>
  );
}


// Strip above the carousel for branch users: what still needs a script, what's ready to post.
function AgentStrip({ onSelectModule }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/social-pro/dashboard`).then((r) => setD(r.data)).catch(() => setD(false));
  }, []);
  if (d === false || d === null) return null;
  return (
    <div className="agent-strip" data-testid="agent-dashboard-strip">
      <button className={`agent-stat ${d.awaiting_script > 0 ? "is-hot" : ""}`} onClick={() => onSelectModule("script")} data-testid="agent-stat-awaiting">
        <span className="agent-stat-icon"><ScrollText size={16} /></span>
        <span className="agent-stat-num">{d.awaiting_script}</span>
        <span className="agent-stat-text"><strong>Awaiting script</strong><small>{d.awaiting_script === 1 ? "Social Pro order needs its script" : "Social Pro orders need scripts"}{d.awaiting?.[0] ? ` · ${d.awaiting[0].address.split(",")[0]}` : ""}</small></span>
        <ArrowRight size={14} className="agent-stat-arrow" />
      </button>
      <div className="agent-stat" data-testid="agent-stat-production">
        <span className="agent-stat-icon"><Hourglass size={16} /></span>
        <span className="agent-stat-num">{d.in_production}</span>
        <span className="agent-stat-text"><strong>In production</strong><small>scripts with the editing team</small></span>
      </div>
      <button className="agent-stat" onClick={() => onSelectModule("social-hub")} data-testid="agent-stat-ready">
        <span className="agent-stat-icon"><PackageCheck size={16} /></span>
        <span className="agent-stat-num">{d.ready_assets}</span>
        <span className="agent-stat-text"><strong>Ready to use</strong><small>{d.latest_asset ? `latest: ${d.latest_asset.title}` : "finished videos in your Social Library"}</small></span>
        <ArrowRight size={14} className="agent-stat-arrow" />
      </button>
      {d.placeholder && <span className="agent-strip-note" data-testid="agent-strip-placeholder">Demo order shown until this branch is linked to WMS</span>}
    </div>
  );
}
